import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { ErrorNotice, LoadingState, SHOWCASE_RANGES, ShowcasePerformance, type ShowcaseRange, type ShowcaseStatsData } from '@bcecloud/portal-ui';
import { errorMessage, getJson } from '@/lib/api';

const RANGE_PARAM = 'days';

/** The partner's showcase numbers, exactly as the partner sees them in their portal. */
export function OrgPartnerPerformancePanel({ organizationId, organizationName }: { readonly organizationId: string; readonly organizationName: string }): React.ReactElement {
  const [params, setParams] = useSearchParams();
  const range: ShowcaseRange = SHOWCASE_RANGES.find((r) => String(r) === params.get(RANGE_PARAM)) ?? 30;
  const stats = useQuery({
    queryKey: ['partner-stats', organizationId, range],
    queryFn: ({ signal }) => getJson<ShowcaseStatsData>(`/organizations/${organizationId}/partner-stats?days=${range}`, undefined, signal),
    placeholderData: keepPreviousData,
  });

  return (
    <div className="mt-6 space-y-4">
      <div>
        <h2 className="text-portal-section font-bold tracking-[-0.4px]">Performance</h2>
        <p className="mt-1 text-portal-small text-portal-muted">
          Views, visitors and leads from {organizationName}'s showcase. {organizationName} sees the same numbers in their portal.
        </p>
      </div>
      {stats.isError ? (
        <ErrorNotice title="Performance unavailable" message={errorMessage(stats.error)} />
      ) : !stats.data ? (
        <LoadingState label="Loading performance…" />
      ) : (
        <ShowcasePerformance
          stats={stats.data}
          range={range}
          subject={organizationName}
          onRangeChange={(next) =>
            setParams(
              (previous) => {
                const out = new URLSearchParams(previous);
                out.set(RANGE_PARAM, String(next));
                return out;
              },
              { replace: true },
            )
          }
        />
      )}
    </div>
  );
}
